import CheckSignOff from '../../components/CheckSignOff';
import SelectAllButton from '../../components/SelectAllButton';
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchChecklistBySlug,
  submitChecklistData,
  fetchSubmissionByDate,
  submitChecklistResult,
  submitBulletResults,
} from "../../../../features/checks/checkActions.js";
import {
  selectStaffList,
  selectUsersLoading,
  selectUsersError,
} from "../../../../features/users/usersSlice";
import { getUsersAsync } from "../../../../features/users/usersActions";
import {
  FiArrowRight,
  FiCheckCircle,
  FiAlertCircle,
  FiCamera,
  FiFileText,
  FiChevronDown,
  FiUser,
  FiUploadCloud,
  FiInfo,
} from "react-icons/fi";
import { getAssetUrl } from "../../../../components/AssetUrl";
import Toast from "../../../../components/Toast";

export default function PatFormChecklist({ slug, selectedDate }) {
  const checkList = useSelector(
    (state) => state.checks.checklistsBySlug[slug] || [],
  );
  const isChecklistLoading = useSelector((state) => state.checks.loading);
  const isChecklistError = useSelector((state) => state.checks.error);
  const checklistTitle = checkList?.name || "Checklist";
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const users = useSelector(selectStaffList);
  const isUsersLoading = useSelector(selectUsersLoading);
  const isUsersError = useSelector(selectUsersError);
  const dispatch = useDispatch();
  const submissionData = useSelector((state) => state.checks.submissionsByDate[`${checkList?.id}:${selectedDate}`]?.data);
  const isChecksLoading = useSelector((state) => state.checks.loading);
  const isChecksError = useSelector((state) => state.checks.error);
  const [signature, setSignature] = useState("");
  const signatureTimeoutRef = useRef(null);
  const [notes, setNotes] = useState("");
  const typingTimeoutRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [itemStatusState, setItemStatusState] = useState({});
  const [itemCommentState, setItemCommentState] = useState({});
  const commentTimeoutRefs = useRef({});
  const [selectingAll, setSelectingAll] = useState(null);
  const bulkInFlight = useRef(false);
  const resource = `${slug}:${selectedDate}`;
  const resourceRef = useRef(resource);
  resourceRef.current = resource;
  useEffect(() => {
    resourceRef.current = resource;
    setSelectingAll(null);
    return () => { resourceRef.current = null; };
  }, [resource]);
  const selectAllYes = async section => {
    if (bulkInFlight.current || !selectedUser || !submissionData?.id) return;
    bulkInFlight.current = true;
    setSelectingAll(section.id);
    try {
      for (const bullet of section.bullets || []) {
        if (resourceRef.current !== resource) break;
        const key = `${section.id}-${bullet.id}`;
        if (itemStatusState[key] === 'YES') continue;
        const data = new FormData();
        data.append('submissionId', String(submissionData.id));
        data.append('checklistItemId', String(section.id));
        data.append('bulletId', String(bullet.id));
        data.append('status', 'YES');
        await dispatch(submitBulletResults(data)).unwrap();
        if (resourceRef.current === resource) setItemStatusState(previous => ({ ...previous, [key]: 'YES' }));
      }
    } catch (error) {
      if (resourceRef.current === resource) Toast('error', 'Unable to select all Yes. Saved answers were retained; please try again.');
    } finally {
      bulkInFlight.current = false;
      if (resourceRef.current === resource) setSelectingAll(null);
    }
  };
  
  // === INTERACTIVE HANDLERS ===
  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setIsDropdownOpen(false);

    if (checkList.id && user.id) {
      const formData = new FormData();

      formData.append("checklistId", Number(checkList.id));
      formData.append("completedById", Number(user.id));
      formData.append("creationDate", selectedDate);

      try {
        // Submit the checklist
        await dispatch(submitChecklistData(formData)).unwrap();
      } catch (error) {
        console.error("Failed to complete checklist submission:", error);
      }
    }
  };

  const handleSignatureChange = (e) => {
    const newSignature = e.target.value;
    setSignature(newSignature); // 1. Update local screen state instantly

    // 2. Wipe previous timeout if manager is still typing actively
    if (signatureTimeoutRef.current) {
      clearTimeout(signatureTimeoutRef.current);
    }

    // 3. Set a clean 1-second delay before pushing data to database
    signatureTimeoutRef.current = setTimeout(() => {
      if (checkList?.id) {
        // Create FormData container for multipart form transmission
        const formData = new FormData();

        formData.append("checklistId", Number(checkList.id));
        formData.append("creationDate", selectedDate);
        formData.append("managerSignature", newSignature);

        // Dispatch the clean FormData payload instance
        dispatch(submitChecklistData(formData));
      }
    }, 1000);
  };

  const handleNotesChange = (e) => {
    const newText = e.target.value;
    setNotes(newText);

    // 2. Clear the previous timer because the user is still actively typing
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // 3. Start a fresh 1-second countdown
    typingTimeoutRef.current = setTimeout(() => {
      if (checkList?.id) {
        // Create FormData container for multipart form transmission
        const formData = new FormData();

        formData.append("checklistId", Number(checkList.id));
        formData.append("creationDate", selectedDate);
        formData.append("openingNotes", newText);

        // Dispatch the clean FormData payload instance
        dispatch(submitChecklistData(formData));
      }
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  // 1. Fetch Staff Personnel Records (Runs once on component mount)
  useEffect(() => {
    const loadStaff = async () => {
      try {
        await dispatch(getUsersAsync("STAFF")).unwrap();
      } catch (err) {
        console.error("Failed to load staff list:", err);
      }
    };
    loadStaff();
  }, [dispatch]);

  useEffect(() => {
    const loadTodaySubmission = async () => {
      if (!checkList?.id) return;
      try {
        await dispatch(
          fetchSubmissionByDate({
            checklistId: checkList.id,
            creationDate: selectedDate,
          }),
        ).unwrap();
      } catch (err) {
        console.error(
          "Failed to locate or instantiate today's submission container:",
          err,
        );
      }
    };
    loadTodaySubmission();
  }, [dispatch, checkList?.id, selectedDate]);

  useEffect(() => {
    if (!submissionData?.id) {
      setSelectedUser(null);
      setNotes("");
      setSignature("");
      setPhotoPreview(null);
      setItemStatusState({});
      setItemCommentState({});
      return;
    }

    setSelectedUser(submissionData.completedBy || null);
    setNotes(submissionData.openingNotes || "");
    setSignature(submissionData.managerSignature || "");
    setPhotoPreview(
      submissionData.photoUrl ? getAssetUrl(submissionData.photoUrl) : null,
    );

    const savedStatuses = {}; 
    const savedComments = {}; 
    if (submissionData.bulletResults) { 
      submissionData.bulletResults.forEach((result) => { 
        const key = `${result.checklistItemId}-${result.bulletId}`; 
        savedStatuses[key] = result.status; 
        savedComments[key] = result.comment || ""; }); } 

        setItemStatusState(savedStatuses); 
        setItemCommentState(savedComments);
  }, [submissionData]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 1. Show instant local preview for a fast UI experience
    const localUrl = URL.createObjectURL(file);
    setPhotoPreview(localUrl);

    // 2. Dispatch the image data to your backend immediately
    if (checkList?.id) {
      const formData = new FormData();
      formData.append("checklistId", Number(checkList.id));
      formData.append("creationDate", selectedDate);
      formData.append("photo", file); // 'photo' matches uploadSingleFile('photo') on backend

      try {
        await dispatch(submitChecklistData(formData)).unwrap();
        console.log("Photo uploaded and synced successfully!");
      } catch (error) {
        console.error("Failed to upload photo:", error);
        // Fallback: Reset preview if the network upload fails
        setPhotoPreview(null);
      }
    } else {
      console.warn("Cannot upload: checklist ID not resolved yet.");
    }
  };

  const handleCommentChange = (sectionId, itemId, comment) => {

    if(!selectedUser){
        Toast('error', 'Please select a staff member to complete this check');
        return;
    }

    const itemKey = `${sectionId}-${itemId}`;

    // Update UI immediately
    setItemCommentState((prev) => ({
      ...prev,
      [itemKey]: comment,
    }));

    // Clear previous timer for this specific item
    if (commentTimeoutRefs.current[itemKey]) {
      clearTimeout(commentTimeoutRefs.current[itemKey]);
    }

    // Save after user stops typing
    commentTimeoutRefs.current[itemKey] = setTimeout(async () => {
      const formData = new FormData();

      formData.append(
        "submissionId",
        String(submissionData.id)
      );

      formData.append(
        "checklistItemId",
        String(sectionId)
      );

      formData.append(
        "bulletId",
        String(itemId)
      );

      // Add comment
      formData.append(
        "comment",
        comment
      );

      try {
        await dispatch(
          submitBulletResults(formData)
        ).unwrap();

        console.log(
          `Comment saved for bullet ${itemId}`
        );
      } catch (error) {
        console.error(
          "Failed to sync comment:",
          error
        );
      }
    }, 800);
  };

  const handleRemovePhoto = async (e) => {
    e.stopPropagation(); // Stops the file browser dialog from reopening

    // 1. Instantly clear out local preview elements
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    // 2. Dispatch to backend to erase the existing photo reference string
    if (checkList?.id) {
      const formData = new FormData();
      formData.append("checklistId", Number(checkList.id));
      formData.append("creationDate", selectedDate);
      formData.append("photoUrl", ""); // Sending an empty string clears the column in Prisma

      try {
        await dispatch(submitChecklistData(formData)).unwrap();
        console.log("Photo reference deleted from database successfully.");
      } catch (error) {
        console.error(
          "Failed to update database photo reference status:",
          error,
        );
      }
    }
  };

  // 2. Fetch Checklist Template Definition Layout (Runs when the route slug changes)
  useEffect(() => {
    const loadTemplate = async () => {
      if (!slug) return;
      try {
        await dispatch(fetchChecklistBySlug(slug)).unwrap();
      } catch (err) {
        console.error(`Failed to load template layout for slug: ${slug}`, err);
      }
    };
    loadTemplate();
  }, [dispatch, slug]);

  const handleStatusChange = async (sectionId, itemId, status) => {

    if(!selectedUser){
        Toast('error', 'Please select a staff member to complete this check');
        return;
    }

    const itemKey = `${sectionId}-${itemId}`;

    // Save current status for rollback
    const previousStatus = itemStatusState[itemKey] ?? null;

    // Immediately update UI
    setItemStatusState((prev) => ({
      ...prev,
      [itemKey]: status,
    }));

    const formData = new FormData();

    formData.append("submissionId", String(submissionData.id));
    formData.append("checklistItemId", String(sectionId));
    formData.append("bulletId", String(itemId));
    formData.append("status", status);

    try {
      await dispatch(submitBulletResults(formData)).unwrap();
    } catch (error) {
      console.error("Failed to sync bullet status:", error);

      // Roll back UI if request fails
      setItemStatusState((prev) => ({
        ...prev,
        [itemKey]: previousStatus,
      }));
    }
  };

  // === VALIDATION RUNTIME EARLY RETURNS BOTTOM OF FILE ===
  // if (isUsersLoading) return <div>Loading User records...</div>;
  if (isUsersError)
    return (
      <div style={{ color: "red" }}>Error fetching users: {isUsersError}</div>
    );
  // if (isChecksLoading) return <div>Loading checklist records...</div>;
  if (isChecksError)
    return (
      <div style={{ color: "red" }}>
        Error fetching checklist: {isChecksError}
      </div>
    );

  return (
    <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm font-sans select-none p-6 overflow-x-auto">
      <fieldset disabled={selectingAll !== null} className="min-w-0">
      {/* SECTION 2: Table Section Titles */}
      <div className="flex items-center justify-between px-2 pt-2 border-b border-slate-100 pb-2">
        <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
          {checklistTitle} Items
        </h3>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            disabled={selectingAll !== null}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`w-full flex items-center justify-between px-3 py-2 bg-white border rounded-lg text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
              isDropdownOpen
                ? "border-blue-500 shadow-sm"
                : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className="flex items-center gap-2 text-slate-700">
              <FiUser
                className={selectedUser ? "text-blue-500" : "text-slate-400"}
              />
              <span
                className={
                  selectedUser ? "font-medium text-slate-900" : "text-slate-400"
                }
              >
                {selectedUser
                  ? selectedUser.fullName
                  : "Select Staff Member..."}
              </span>
            </div>
            <FiChevronDown
              className={`text-slate-400 transition-transform duration-150 ${isDropdownOpen ? "rotate-180 text-blue-500" : ""}`}
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto py-1">
              {users && users.length > 0 ? (
                users.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      handleSelectUser(user);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                      selectedUser?.id === user.id
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="truncate">{user.fullName}</span>
                    {selectedUser?.id === user.id && (
                      <FiCheckCircle className="text-blue-500 shrink-0 ml-2" />
                    )}
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-xs text-center text-slate-400 italic">
                  No users found
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="min-w-[800px]">
        {/* Table Headings */}
        <div className="grid grid-cols-12 gap-4 pb-3 border-b border-gray-100 text-[10px] font-black text-slate-500 uppercase tracking-wider items-center">
          <div className="col-span-5 text-left pl-2">Check Item</div>
          <div className="col-span-3 grid grid-cols-3 text-center">
            <span>Yes</span>
            <span>No</span>
            <span>N/A</span>
          </div>
          <div className="col-span-4 text-left pl-4">
            Comments / Evidence (Optional)
          </div>
        </div>

        <div className="mt-4 space-y-6">
          {checkList.items?.map((section) => (
            <div key={section.id} className="space-y-2">
              <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-black text-blue-900 text-left pl-1 pt-2">
                {section.title}
              </h3>
              {section.bullets?.length > 0 && <SelectAllButton section={section.title} onClick={() => selectAllYes(section)} busy={selectingAll === section.id}
                disabled={selectingAll !== null || !selectedUser || !submissionData?.id || !section.bullets?.length ||
                  section.bullets.every(bullet => itemStatusState[`${section.id}-${bullet.id}`] === 'YES')} />}
              </div>

              <div className="space-y-1">
                {section.bullets?.map((item) => {
                  const itemKey = `${section.id}-${item.id}`;

                  return (
                    <div
                      key={item.id}
                      className="grid grid-cols-12 gap-4 items-center bg-white hover:bg-slate-50/40 p-1.5 rounded-xl transition duration-100"
                    >
                      {/* Checklist Item */}
                      <div className="col-span-5 flex items-start space-x-3 text-xs font-bold text-slate-800 text-left pl-2 leading-relaxed">
                        <span className="line-clamp-2">{item.text}</span>
                      </div>

                      {/* YES / NO / NA */}
                      <div className="col-span-3 grid grid-cols-3 items-center justify-center">
                        {["YES", "NO", "NA"].map((type) => (
                          <div key={type} className="flex justify-center">
                            <input
                              type="radio"
                              aria-label={`${type}: ${item.text}`}
                              disabled={selectingAll !== null}
                              name={`choice-${itemKey}`}
                              value={type}
                              checked={itemStatusState[itemKey] === type}
                              onChange={() =>
                                handleStatusChange(section.id, item.id, type)
                              }
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20 cursor-pointer accent-blue-600"
                            />
                          </div>
                        ))}
                      </div>

                      {/* Comments - leave disabled for now */}
                      <div className="col-span-4 flex items-center space-x-2 pl-4">
                        <input
                          type="text"
                          value={itemCommentState[itemKey] || ""}
                          onChange={(e) =>
                            handleCommentChange(
                              section.id,
                              item.id,
                              e.target.value
                            )
                          }
                          placeholder="Add comments..."
                          className="w-full text-xs px-3 py-2 border border-gray-200 rounded-lg placeholder-slate-400 bg-slate-50/20 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                        />
                        <button
                          type="button"
                          disabled
                          className="p-2 border border-gray-200 rounded-lg text-slate-300 cursor-not-allowed"
                        >
                          <FiCamera className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Manager Signature Alignment Section */}
        {/* Signature Box */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
            Manager signature
          </label>
          <div className="flex items-center gap-4 bg-slate-50/50 border border-slate-100 rounded-xl p-3 h-14 relative overflow-hidden">
            <input
              type="text"
              value={signature || ""}
              onChange={handleSignatureChange}
              placeholder="Type name to sign..."
              className="font-serif italic text-xl text-slate-700 bg-transparent pl-2 tracking-wide focus:outline-none w-full pr-12 placeholder:text-slate-300 placeholder:not-italic placeholder:text-xs placeholder:font-sans placeholder:font-normal"
            />
            {signature && (
              <button
                type="button"
                onClick={() => handleSignatureChange({ target: { value: "" } })} // Reuse sync handler to wipe out database record cleanly
                className="absolute right-3 text-[10px] text-red-500 hover:text-red-600 font-bold tracking-wide"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Bottom Document Metadata Attachments */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-100">
          {/* Notes Area Block */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
              {checklistTitle} notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={handleNotesChange}
              placeholder={`Add any notes about today's ${checklistTitle.toLowerCase()}...`}
              className="w-full border border-slate-200 rounded-xl p-3 text-xs text-slate-700 bg-white placeholder-slate-300 focus:outline-none focus:border-indigo-500 min-h-[76px] resize-none shadow-inner"
            />
          </div>

          {/* Upload Attachments */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
              Add photo (optional)
            </label>

            {/* Hidden Native File Input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoChange}
              accept="image/*"
              className="hidden"
            />

            {photoPreview ? (
              /* Photo Preview State View Container Layout Box */
              <div className="relative w-full border border-slate-200 bg-slate-100 rounded-xl h-[140px] overflow-hidden group">
                <img
                  src={photoPreview}
                  alt="Submission preview"
                  className="w-full h-full object-cover"
                />
                {/* Hover overlay utility curtain panels */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 bg-white text-slate-800 rounded-lg text-xs font-bold shadow hover:bg-slate-50"
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold shadow hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              /* Fallback Interactive Blank Empty State Target Control Slot */
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-full border border-dashed border-slate-200 bg-slate-50/50 rounded-xl p-4 flex flex-col items-center justify-center gap-2 h-[76px] cursor-pointer hover:bg-slate-50 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center gap-1.5 text-indigo-600 font-bold text-xs">
                  <FiCamera className="text-sm" />
                  <span>Upload photo</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <fieldset disabled={selectingAll !== null}>
        <CheckSignOff checklistId={checkList?.id} creationDate={selectedDate}
          fields={{ openingNotes: notes, managerSignature: signature, completedById: selectedUser?.id }} />
        </fieldset>

        {/* Bottom Security Audit Banner Notice */}
        <div className="mt-8 flex items-center space-x-2 text-[11px] text-blue-800/80 font-semibold bg-blue-50/40 border border-blue-100/40 p-3 rounded-xl">
          <FiInfo className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <p className="text-left">
            All PAT records are time stamped and securely stored for compliance
            and audit purposes.
          </p>
        </div>
      </div>
      </fieldset>
    </div>
  );
}
